import { Component, Input, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  PartnerAccessService,
  PartnerDto,
  PartnerAccessMatrixVm,
  PolicyRecordDto,
  PolicyRecordsResult,
  SavePartnerAccessRequest,
  AttachmentFolderDto,
  AttachmentFileDto,
  AccessNodeDto,
  RevokePartnerAssignmentsRequest,
  DataSetDto,
  PartnerUserDto,
  ClientOptionDto
} from '../../../services/PartnerAccessService';

export type PartnerTab = 'records' | 'attachments';

@Component({
  selector: 'app-partner-assignment',
  templateUrl: './partner-assignment.component.html',
  styleUrls: ['./partner-assignment.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})export class PartnerAssignmentComponent implements OnInit {

  @Input() clientId!: string;
  @Input() dataSetId?: string | null;

  dataSets: DataSetDto[] = [];
  selectedDataSetId: string | null = null;

  // Partner management
  createPartnerMode = false;
  newPartnerCode = '';
  newPartnerName = '';
  newPartnerRefClientId: string | null = null;
  availableClients: ClientOptionDto[] = [];

  // Context menu
  menuPartner: PartnerDto | null = null;
  menuX = 0;
  menuY = 0;

  // Assign client
  assigningClientPartner: PartnerDto | null = null;
  assignClientRefId: string | null = null;

  // User management
  managingUsersPartner: PartnerDto | null = null;
  partnerUsers: PartnerUserDto[] = [];
  availableUsers: PartnerUserDto[] = [];
  userToAdd = '';

  activeTab: 'records' | 'attachments' = 'records';
  dataPolicyId = '';
  loading = false;
  allPartners: PartnerDto[] = [];
  selectedPartnerIds = new Set<string>();
  activePartnerId: string | null = null;
  partnerAccessCache = new Map<string, PartnerAccessMatrixVm>();
  allRecords: PolicyRecordDto[] = [];
  selectedRecordIds = new Set<string>();
  page = 1;
  pageSize = 5;
  // Page size used to pull the entire dataset in a single request (see loadPolicyRecords).
  // Deliberately large: the server applies no pageSize cap, so one big page avoids the
  // many sequential round-trips a page-by-page loop would incur.
  private readonly recordFetchPageSize = 100000;
  partnerPage = 1;
  partnerPageSize = 5;
  searchTerm = '';
  partnerSearchTerm = '';
  folderTree: AttachmentFolderDto[] = [];
  allAttachments: AttachmentFileDto[] = [];
  selectedHierarchyIds = new Set<string>();
  selectedAttachmentIds = new Set<string>();
  // Always stored lowercase — all map keys derived from this are consistent
  selectedRecordId: string | null = null;
  collapsedFolderIds = new Set<string>();
  partnerRecordMap = new Map<string, string[]>();
  recordNameCache = new Map<string, string>();
  recordMap = new Map<string, PolicyRecordDto>();

  // Keyed by partnerId → recordId (lowercase) → Set of selected IDs
  private recordHierarchySelections = new Map<string, Map<string, Set<string>>>();
  private recordAttachmentSelections = new Map<string, Map<string, Set<string>>>();
  private folderParentMap = new Map<string, string | null>(); // childId → parentId (null = root)
  folderFileCount = new Map<string, number>();
  folderFileMap = new Map<string, AttachmentFileDto[]>();
  summarySearchTerm = '';
  summaryPage = 1;
  summaryPageSize = 10;
  revokeSelectedIds = new Set<string>();
  attachmentRecordSearchTerm = '';
  attachmentRecordPage = 1;
  attachmentRecordPageSize = 10;


  @ViewChild('usersPanel') usersPanelRef?: ElementRef;
  @ViewChild('assignPanel') assignPanelRef?: ElementRef;

  constructor(private partnerAccess: PartnerAccessService) { }

  get filteredRecords(): PolicyRecordDto[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.allRecords;
    return this.allRecords.filter(r =>
      this.getWellName(r).toLowerCase().includes(term) ||
      this.getApi(r).toLowerCase().includes(term) ||
      this.getFieldName(r).toLowerCase().includes(term)
    );
  }

  get totalCount(): number { return this.filteredRecords.length; }
  get totalPages(): number { return Math.max(1, Math.ceil(this.totalCount / this.pageSize)); }

  get records(): PolicyRecordDto[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredRecords.slice(start, start + this.pageSize);
  }

  get filteredPartners(): PartnerDto[] {
    const term = this.partnerSearchTerm.trim().toLowerCase();
    if (!term) return this.allPartners;
    return this.allPartners.filter(p =>
      p.name.toLowerCase().includes(term) ||
      (p.code ?? '').toLowerCase().includes(term) ||
      (p.refClientName ?? '').toLowerCase().includes(term)
    );
  }

  get partnerTotalCount(): number { return this.filteredPartners.length; }
  get partnerTotalPages(): number { return Math.max(1, Math.ceil(this.partnerTotalCount / this.partnerPageSize)); }

  get partners(): PartnerDto[] {
    const start = (this.partnerPage - 1) * this.partnerPageSize;
    return this.filteredPartners.slice(start, start + this.partnerPageSize);
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    if (this.dataSetId) {
      this.selectedDataSetId = this.dataSetId;
      this.loadDataPolicyId();
      this.loadPartners();
      this.loadPolicyRecords();
    } else {
      this.partnerAccess.getDataSets(this.clientId, datasets => {
        this.dataSets = datasets;
        if (datasets.length > 0) {
          this.selectedDataSetId = datasets[0].id;
        }
        this.loadDataPolicyId();
        this.loadPartners();
        this.loadPolicyRecords();
      });
    }
  }

  onDataSetChange(id: string): void {
    this.selectedDataSetId = id || null;
    this.allRecords = [];
    this.recordMap.clear();
    this.recordNameCache.clear();
    this.partnerRecordMap.clear();
    this.page = 1;
    this.loadPolicyRecords();
    this.loadPartners();
  }

  switchTab(tab: 'records' | 'attachments'): void {
    this.activeTab = tab;
    if (tab === 'attachments' && this.selectedRecordId && this.folderTree.length === 0) {
      this.loadFolderTree(this.selectedRecordId);
    }
  }

  private loadDataPolicyId(): void {
    this.partnerAccess.getDataPolicyId(this.clientId, id => {
      this.dataPolicyId = id === '00000000-0000-0000-0000-000000000000' ? '' : id;
      if (this.activePartnerId) {
        this.loadPartnerAccess(this.activePartnerId);
      }
    });
  }

  loadPartners(): void {
    this.partnerAccess.getAllPartners(
      this.clientId,
      partners => {
        this.allPartners = partners;
        if (this.allPartners.length > 0 && this.selectedPartnerIds.size === 0) {
          const first = this.allPartners[0].id;
          this.selectedPartnerIds.add(first);
          this.setActivePartner(first);
        }
      }
    );
  }


  get selectedPartnerMatrix() {
    return this.allPartners
      .filter(p => this.selectedPartnerIds.has(p.id))
      .map(p => ({
        partnerId: p.id,
        partnerName: p.name,
        records: (this.partnerRecordMap.get(p.id) ?? [])
          .map(id => this.recordMap.get(id.toLowerCase()))
          .filter((r): r is PolicyRecordDto => !!r)
      }));
  }

  private loadFolderTree(recordId: string): void {
    this.partnerAccess.getHierarchyTree(
      this.clientId,
      recordId,
      res => {
        this.folderTree = res ?? [];
        this.partnerAccess.getRecordAttachments(
          this.clientId,
          recordId,
          attachments => {
            this.allAttachments = attachments ?? [];
            this.collapsedFolderIds.clear();
            this.collapseAllFolders(this.folderTree);
            this.folderParentMap.clear();
            this.buildParentMap(this.folderTree);
            this.buildFolderIndexes();
            this.restoreAttachmentSelections();
          },
          undefined,
          this.dataSetId
        );
      },
      undefined,
      this.dataSetId
    );
  }
  private buildFolderIndexes(): void {
    // Step 1: O(n) pass — direct files per folder, no filtering in the tree walk
    this.folderFileMap.clear();
    this.folderFileCount.clear();
    for (const file of this.allAttachments) {
      const key = (file.hierarchyId ?? '').toLowerCase();
      if (!key) continue;
      const list = this.folderFileMap.get(key);
      if (list) {
        list.push(file);
      } else {
        this.folderFileMap.set(key, [file]);
      }
    }
    // Step 2: recursive walk — cumulative counts (direct + descendants) using the map
    const countSubtree = (folders: AttachmentFolderDto[]): number => {
      let total = 0;
      for (const folder of folders) {
        const key = folder.id.toLowerCase();
        const direct = this.folderFileMap.get(key)?.length ?? 0;
        const children = countSubtree(folder.children ?? []);
        this.folderFileCount.set(key, direct + children);
        total += direct + children;
      }
      return total;
    };
    countSubtree(this.folderTree);
  }

  private collapseAllFolders(folders: AttachmentFolderDto[]): void {
    for (const folder of folders) {
      this.collapsedFolderIds.add(folder.id);

      if (folder.children?.length) {
        this.collapseAllFolders(folder.children);
      }
    }
  }
  private collectTreeFolderIds(folders: AttachmentFolderDto[], out = new Set<string>()): Set<string> {
    for (const f of folders) {
      out.add(f.id.toLowerCase());
      this.collectTreeFolderIds(f.children, out);
    }
    return out;
  }

  private collectTreeAttachmentIds(folders: AttachmentFolderDto[], out = new Set<string>()): Set<string> {
    for (const folder of folders) {
      const files = this.folderFileMap.get(folder.id.toLowerCase()) ?? [];
      for (const f of files) out.add(f.id);
      this.collectTreeAttachmentIds(folder.children, out);
    }
    return out;
  }

  private persistCurrentRecordSelections(): void {
    if (!this.activePartnerId || !this.selectedRecordId) return;
    const partnerId = this.activePartnerId;
    const key = this.selectedRecordId; // already lowercase

    let hMap = this.recordHierarchySelections.get(partnerId);
    if (!hMap) { hMap = new Map(); this.recordHierarchySelections.set(partnerId, hMap); }
    hMap.set(key, new Set(this.selectedHierarchyIds));

    let aMap = this.recordAttachmentSelections.get(partnerId);
    if (!aMap) { aMap = new Map(); this.recordAttachmentSelections.set(partnerId, aMap); }
    aMap.set(key, new Set(this.selectedAttachmentIds));
  }

  private restoreAttachmentSelections(): void {
    if (!this.activePartnerId || !this.selectedRecordId) return;
    const partnerId = this.activePartnerId;
    const key = this.selectedRecordId; // already lowercase

    const hMap = this.recordHierarchySelections.get(partnerId);
    const aMap = this.recordAttachmentSelections.get(partnerId);

    // Use local unsaved state if the user already visited this record.
    if (hMap?.has(key)) {
      this.selectedHierarchyIds  = new Set(hMap.get(key));
      this.selectedAttachmentIds = new Set(aMap?.get(key) ?? []);
      return;
    }

    const matrix = this.partnerAccessCache.get(partnerId);
    if (!matrix) return;

    this.selectedHierarchyIds = new Set(
      (matrix.hierarchies ?? [])
        .filter(h => !h.negate && h.recordId?.toLowerCase() === key)
        .map(h => h.id)
    );
    this.selectedAttachmentIds = new Set(
      (matrix.attachments ?? [])
        .filter(a => !a.negate && a.recordId?.toLowerCase() === key)
        .map(a => a.id)
    );

    // Seed per-record maps so saveAttachmentAssignment sees the server state on first visit.
    let seedH = this.recordHierarchySelections.get(partnerId);
    if (!seedH) { seedH = new Map(); this.recordHierarchySelections.set(partnerId, seedH); }
    seedH.set(key, new Set(this.selectedHierarchyIds));

    let seedA = this.recordAttachmentSelections.get(partnerId);
    if (!seedA) { seedA = new Map(); this.recordAttachmentSelections.set(partnerId, seedA); }
    seedA.set(key, new Set(this.selectedAttachmentIds));
  }

  isFolderEmpty(folder: AttachmentFolderDto): boolean {
    const count = this.folderFileCount.get(folder.id.toLowerCase()) ?? 0;
    return count === 0 && !folder.children?.length;
  }

  isFolderCollapsed(folderId: string): boolean {
    return this.collapsedFolderIds.has(folderId);
  }

  toggleFolderCollapse(folderId: string, event: Event): void {
    event.stopPropagation();
    if (this.collapsedFolderIds.has(folderId)) {
      this.collapsedFolderIds.delete(folderId);
    } else {
      this.collapsedFolderIds.add(folderId);
    }
  }

  private buildParentMap(folders: AttachmentFolderDto[], parentId: string | null = null): void {
    for (const f of folders) {
      this.folderParentMap.set(f.id, parentId);
      this.buildParentMap(f.children, f.id);
    }
  }

  private findFolder(id: string, folders: AttachmentFolderDto[]): AttachmentFolderDto | null {
    for (const f of folders) {
      if (f.id === id) return f;
      const found = this.findFolder(id, f.children);
      if (found) return found;
    }
    return null;
  }

  private propagateUncheckUp(folderId: string): void {
    const parentId = this.folderParentMap.get(folderId);
    if (!parentId) return;
    const parent = this.findFolder(parentId, this.folderTree);
    if (!parent) return;
    const allChildrenUnchecked = parent.children.every(c => !this.selectedHierarchyIds.has(c.id));
    if (allChildrenUnchecked && this.selectedHierarchyIds.has(parentId)) {
      this.selectedHierarchyIds.delete(parentId);
      this.propagateUncheckUp(parentId);
    }
  }

  private propagateCheckUp(folderId: string): void {
    const parentId = this.folderParentMap.get(folderId);
    if (!parentId) return;
    const parent = this.findFolder(parentId, this.folderTree);
    if (!parent) return;
    const allChildrenChecked = parent.children.every(c => this.selectedHierarchyIds.has(c.id));
    if (allChildrenChecked && !this.selectedHierarchyIds.has(parentId)) {
      this.selectedHierarchyIds.add(parentId);
      this.propagateCheckUp(parentId);
    }
  }

  isHierarchySelected(folderId: string): boolean {
    return this.selectedHierarchyIds.has(folderId);
  }

  toggleHierarchy(folderId: string): void {
    if (!this.activePartnerId || !this.selectedRecordId) return;
    const partnerId = this.activePartnerId;
    const key = this.selectedRecordId; // already lowercase

    if (this.selectedHierarchyIds.has(folderId)) {
      this.selectedHierarchyIds.delete(folderId);

      const folder = this.findFolder(folderId, this.folderTree);
      if (folder) {
        this.collectTreeFolderIds(folder.children).forEach(id => this.selectedHierarchyIds.delete(id));
        this.collectTreeAttachmentIds([folder]).forEach(id => this.selectedAttachmentIds.delete(id));
      }

      this.propagateUncheckUp(folderId);

      let aMap = this.recordAttachmentSelections.get(partnerId);
      if (!aMap) { aMap = new Map(); this.recordAttachmentSelections.set(partnerId, aMap); }
      aMap.set(key, new Set(this.selectedAttachmentIds));
    } else {
      this.selectedHierarchyIds.add(folderId);

      const folder = this.findFolder(folderId, this.folderTree);
      if (folder) {
        this.collectTreeFolderIds(folder.children).forEach(id => this.selectedHierarchyIds.add(id));
        this.collectTreeAttachmentIds([folder]).forEach(id => this.selectedAttachmentIds.add(id));
      }

      this.propagateCheckUp(folderId);

      let aMap = this.recordAttachmentSelections.get(partnerId);
      if (!aMap) { aMap = new Map(); this.recordAttachmentSelections.set(partnerId, aMap); }
      aMap.set(key, new Set(this.selectedAttachmentIds));
    }

    let hMap = this.recordHierarchySelections.get(partnerId);
    if (!hMap) { hMap = new Map(); this.recordHierarchySelections.set(partnerId, hMap); }
    hMap.set(key, new Set(this.selectedHierarchyIds));
  }

  toggleAttachmentFile(attachmentId: string): void {
    if (!this.activePartnerId || !this.selectedRecordId) return;
    const partnerId = this.activePartnerId;
    const key = this.selectedRecordId; // already lowercase

    if (this.selectedAttachmentIds.has(attachmentId)) {
      this.selectedAttachmentIds.delete(attachmentId);
    } else {
      this.selectedAttachmentIds.add(attachmentId);
    }

    let aMap = this.recordAttachmentSelections.get(partnerId);
    if (!aMap) { aMap = new Map(); this.recordAttachmentSelections.set(partnerId, aMap); }
    aMap.set(key, new Set(this.selectedAttachmentIds));
  }

  isAttachmentSelected(attachmentId: string): boolean {
    return this.selectedAttachmentIds.has(attachmentId);
  }

  getRecordName(recordId: string): string {
    const key = recordId.toLowerCase();

    const record = this.recordMap.get(key);
    if (record) {
      return this.getWellName(record);
    }

    const cachedName = this.recordNameCache.get(key);
    if (cachedName) {
      return cachedName;
    }

    console.warn('Record name not found in cache:', recordId);

    return 'Loading...';
  }

  get activePartnerRecords(): PolicyRecordDto[] {
    return this.activePartnerRecordIds
      .map(id => this.recordMap.get(id.toLowerCase()))
      .filter((r): r is PolicyRecordDto => !!r);
  }

  get summaryAllRecords(): PolicyRecordDto[] {
    return this.selectedPartnerMatrix[0]?.records ?? [];
  }

  get summaryFilteredRecords(): PolicyRecordDto[] {
    if (!this.summarySearchTerm) return this.summaryAllRecords;
    const term = this.summarySearchTerm.toLowerCase();
    return this.summaryAllRecords.filter(r =>
      this.getWellName(r).toLowerCase().includes(term) ||
      this.getApi(r).toLowerCase().includes(term) ||
      this.getFieldName(r).toLowerCase().includes(term)
    );
  }

  get summaryTotalCount(): number { return this.summaryFilteredRecords.length; }

  get summaryTotalPages(): number {
    return Math.max(1, Math.ceil(this.summaryTotalCount / this.summaryPageSize));
  }

  get summaryPagedRecords(): PolicyRecordDto[] {
    const page = Math.min(this.summaryPage, this.summaryTotalPages);
    const start = (page - 1) * this.summaryPageSize;
    return this.summaryFilteredRecords.slice(start, start + this.summaryPageSize);
  }

  get summaryRangeLabel(): string {
    if (this.summaryTotalCount === 0) return '0 records';
    const start = (this.summaryPage - 1) * this.summaryPageSize + 1;
    const end = Math.min(this.summaryPage * this.summaryPageSize, this.summaryTotalCount);
    return `${start}–${end} of ${this.summaryTotalCount}`;
  }

  onSummarySearch(value: string): void {
    this.summarySearchTerm = value.trim();
    this.summaryPage = 1;
  }

  clearSummarySearch(): void {
    this.summarySearchTerm = '';
    this.summaryPage = 1;
  }

  get filteredAttachmentRecords(): PolicyRecordDto[] {
    const term = this.attachmentRecordSearchTerm.trim().toLowerCase();
    if (!term) return this.activePartnerRecords;
    return this.activePartnerRecords.filter(r =>
      this.getWellName(r).toLowerCase().includes(term) ||
      this.getApi(r).toLowerCase().includes(term) ||
      this.getFieldName(r).toLowerCase().includes(term)
    );
  }

  get attachmentRecordTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredAttachmentRecords.length / this.attachmentRecordPageSize));
  }

  get pagedAttachmentRecords(): PolicyRecordDto[] {
    const page = Math.min(this.attachmentRecordPage, this.attachmentRecordTotalPages);
    const start = (page - 1) * this.attachmentRecordPageSize;
    return this.filteredAttachmentRecords.slice(start, start + this.attachmentRecordPageSize);
  }

  get attachmentRecordRangeLabel(): string {
    const total = this.filteredAttachmentRecords.length;
    if (total === 0) return '0 records';
    const start = (this.attachmentRecordPage - 1) * this.attachmentRecordPageSize + 1;
    const end = Math.min(this.attachmentRecordPage * this.attachmentRecordPageSize, total);
    return `${start}–${end} of ${total}`;
  }

  onAttachmentRecordSearch(value: string): void {
    this.attachmentRecordSearchTerm = value;
    this.attachmentRecordPage = 1;
  }

  clearAttachmentRecordSearch(): void {
    this.attachmentRecordSearchTerm = '';
    this.attachmentRecordPage = 1;
  }

  goToAttachmentRecordPage(page: number): void {
    if (page < 1 || page > this.attachmentRecordTotalPages || page === this.attachmentRecordPage) return;
    this.attachmentRecordPage = page;
  }

  trackByPartnerId(_: number, item: { partnerId: string }): string {
    return item.partnerId;
  }

  goToSummaryPage(page: number): void {
    if (page < 1 || page > this.summaryTotalPages || page === this.summaryPage) return;
    this.summaryPage = page;
  }

  toggleRevokeSelect(recordId: string): void {
    const key = recordId.toLowerCase();
    if (this.revokeSelectedIds.has(key)) {
      this.revokeSelectedIds.delete(key);
    } else {
      this.revokeSelectedIds.add(key);
    }
  }

  isRevokeSelected(recordId: string): boolean {
    return this.revokeSelectedIds.has(recordId.toLowerCase());
  }

  revokeSelected(): void {
    if (!this.activePartnerId || !this.dataPolicyId || this.revokeSelectedIds.size === 0) return;
    const partnerId = this.activePartnerId;
    const keys = Array.from(this.revokeSelectedIds);

    this.partnerAccess.revokeAccess(this.clientId, {
      partnerId,
      dataPolicyId: this.dataPolicyId,
      recordIds: keys
    }, () => {
      keys.forEach(key => {
        this.recordHierarchySelections.get(partnerId)?.delete(key);
        this.recordAttachmentSelections.get(partnerId)?.delete(key);
      });

      if (this.selectedRecordId && keys.includes(this.selectedRecordId)) {
        this.selectedRecordId = null;
        this.selectedHierarchyIds.clear();
        this.selectedAttachmentIds.clear();
        this.folderTree = [];
      }

      this.revokeSelectedIds.clear();
      this.summaryPage = 1;
      this.loadPartnerAccess(partnerId);
    });
  }

  togglePartner(partner: PartnerDto): void {
    const id = partner.id;
    this.selectedPartnerIds.clear();
    this.selectedPartnerIds.add(id);
    this.selectedRecordId = null;
    this.folderTree = [];
    this.allAttachments = [];
    this.selectedHierarchyIds.clear();
    this.selectedAttachmentIds.clear();
    this.recordHierarchySelections.clear();
    this.recordAttachmentSelections.clear();
    this.summarySearchTerm = '';
    this.summaryPage = 1;
    this.revokeSelectedIds.clear();
    this.attachmentRecordSearchTerm = '';
    this.attachmentRecordPage = 1;
    this.setActivePartner(id);

    if (this.managingUsersPartner && this.managingUsersPartner.id !== id) {
      this.managingUsersPartner = partner;
      this.partnerUsers = [];
      this.availableUsers = [];
      this.userToAdd = '';
      this.loadPartnerUsers(partner);
      this.loadAvailableUsers(partner);
    }

    if (this.assigningClientPartner && this.assigningClientPartner.id !== id) {
      this.assigningClientPartner = partner;
      this.assignClientRefId = partner.refClientId ?? null;
    }
  }

  isPartnerSelected(id: string): boolean {
    return this.selectedPartnerIds.has(id);
  }

  isActivePartner(id: string): boolean {
    return this.activePartnerId === id;
  }

  private setActivePartner(partnerId: string): void {
    this.activePartnerId = partnerId;
    this.loadPartnerAccess(partnerId);
  }

  private loadPartnerAccess(partnerId: string): void {
    if (!this.dataPolicyId) return;

    this.partnerAccess.getAccessMatrix(
      this.clientId,
      this.dataPolicyId,
      partnerId,
      res => {
        this.partnerAccessCache.set(partnerId, res);

        if (this.activePartnerId === partnerId) {
          this.syncRecordsFromAccess(res);
          if (this.activeTab === 'attachments') {
            this.restoreAttachmentSelections();
          }
        }

        this.partnerAccess.getPartnerRecordAccess(
          this.clientId,
          partnerId,
          recordIds => {
            this.partnerRecordMap.set(partnerId, recordIds.map(id => id.toLowerCase()));
          }
        );
      }
    );
  }

  private syncRecordsFromAccess(matrix: PartnerAccessMatrixVm): void {
    this.selectedRecordIds = new Set(
      (matrix.recordIds ?? []).map(r => r.toLowerCase())
    );
  }

  get activePartnerAccess(): PartnerAccessMatrixVm | undefined {
    if (!this.activePartnerId) return undefined;
    return this.partnerAccessCache.get(this.activePartnerId);
  }

  get activePartnerName(): string {
    return this.allPartners.find(p => p.id === this.activePartnerId)?.name ?? '';
  }

  // Pull the whole dataset in one request so allRecords holds every record — the
  // client-side pager, recordMap, and the assigned-records summary all assume the
  // full record set is in memory.
  loadPolicyRecords(): void {
    this.loading = true;
    this.allRecords = [];

    this.partnerAccess.getPolicyRecords(
      this.clientId,
      this.selectedDataSetId,
      res => {
        res.records.forEach(r => {
          const key = r.id.toLowerCase();
          this.recordMap.set(key, r);
          this.recordNameCache.set(key, this.getWellName(r));
        });
        this.allRecords = res.records;

        // Surface truncation rather than silently showing a partial list. If this
        // ever fires, the dataset has outgrown fetch-all and needs server-side paging.
        if (res.totalCount > res.records.length) {
          console.warn(
            `Policy records truncated: loaded ${res.records.length} of ${res.totalCount}.`
          );
        }

        this.page    = 1;
        this.loading = false;
      },
      () => { this.loading = false; },
      1,
      this.recordFetchPageSize
    );
  }

  private searchPolicyRecords(term: string): void {
    this.loading = true;
    this.partnerAccess.getPolicyRecords(
      this.clientId,
      this.selectedDataSetId,
      res => {
        // Merge server results into allRecords without duplicating
        res.records.forEach(r => {
          const key = r.id.toLowerCase();
          if (!this.recordMap.has(key)) {
            this.allRecords.push(r);
            this.recordMap.set(key, r);
            this.recordNameCache.set(key, this.getWellName(r));
          }
        });
        this.page    = 1;
        this.loading = false;
      },
      undefined,
      1,
      100,
      term
    );
  }

  toggleRecord(recordId: string): void {
    const key = recordId.toLowerCase();
    if (this.selectedRecordIds.has(key)) {
      this.selectedRecordIds.delete(key);
    } else {
      this.selectedRecordIds.add(key);
    }
  }

  isRecordSelected(id: string): boolean {
    return this.selectedRecordIds.has(id.toLowerCase());
  }

  get activePartnerRecordIds(): string[] {
    return this.activePartnerId ? (this.partnerRecordMap.get(this.activePartnerId) ?? []) : [];
  }

  isSelectedForAttachment(recordId: string): boolean {
    return this.selectedRecordId === recordId.toLowerCase();
  }

  save(): void {
    if (this.selectedPartnerIds.size === 0 || !this.dataPolicyId) return;

    const payload: SavePartnerAccessRequest = {
      partnerIds: Array.from(this.selectedPartnerIds),
      recordIds: Array.from(this.selectedRecordIds),
      dataPolicyId: this.dataPolicyId
    };

    this.partnerAccess.savePartnerRecordAccess(this.clientId, payload, () => {
      for (const pid of this.selectedPartnerIds) {
        this.loadPartnerAccess(pid);
      }
      if (this.selectedRecordId) {
        this.loadFolderTree(this.selectedRecordId);
      }
    });
  }

  get canSaveAttachments(): boolean {
    return !!this.activePartnerId && !!this.selectedRecordId;
  }

  saveAttachmentAssignment(): void {
    if (!this.activePartnerId || !this.selectedRecordId?.trim()) return;

    this.persistCurrentRecordSelections();

    const partnerId = this.activePartnerId;
    const key = this.selectedRecordId;

    const hMap = this.recordHierarchySelections.get(partnerId);
    const aMap = this.recordAttachmentSelections.get(partnerId);

    const hierarchySet = hMap?.get(key) ?? new Set<string>();
    const attachmentSet = aMap?.get(key) ?? new Set<string>();

    const allHierarchyIds = this.collectTreeFolderIds(this.folderTree);
    const hierarchyNodes: AccessNodeDto[] =
      Array.from(allHierarchyIds).map(id => ({
        id,
        negate: !hierarchySet.has(id),
        type: 'hierarchy'
      }));

    const allAttachmentIds = this.collectTreeAttachmentIds(this.folderTree);
    const attachmentNodes: AccessNodeDto[] =
      Array.from(allAttachmentIds).map(id => ({
        id,
        negate: !attachmentSet.has(id),
        type: 'attachment'
      }));

    this.partnerAccess.saveHierarchyAccess(
      partnerId,
      key,
      hierarchyNodes,
      () => {
        this.partnerAccess.saveAttachmentAccess(
          partnerId,
          key,
          attachmentNodes,
          () => {
            this.refreshRecordAccessNodes(partnerId, key);
          }
        );
      }
    );
  }

  revokeRecordAccess(recordId: string): void {
    if (!this.activePartnerId || !this.dataPolicyId) return;
    const partnerId = this.activePartnerId;
    const key = recordId.toLowerCase();

    this.partnerAccess.revokeAccess(this.clientId, {
      partnerId,
      dataPolicyId: this.dataPolicyId,
      recordIds: [key]
    }, () => {
      this.recordHierarchySelections.get(partnerId)?.delete(key);
      this.recordAttachmentSelections.get(partnerId)?.delete(key);
      this.revokeSelectedIds.delete(key);

      if (this.selectedRecordId === key) {
        this.selectedRecordId = null;
        this.selectedHierarchyIds.clear();
        this.selectedAttachmentIds.clear();
        this.folderTree = [];
      }

      this.loadPartnerAccess(partnerId);
    });
  }

  onPartnerPageSizeChange(size: number): void {
    this.partnerPageSize = size;
    this.partnerPage = 1;
  }

  goToPartnerPage(page: number): void {
    if (page < 1 || page > this.partnerTotalPages || page === this.partnerPage) return;
    this.partnerPage = page;
  }

  get partnerRangeLabel(): string {
    if (this.partnerTotalCount === 0) return '0 partners';
    const start = (this.partnerPage - 1) * this.partnerPageSize + 1;
    const end   = Math.min(this.partnerPage * this.partnerPageSize, this.partnerTotalCount);
    return `${start}–${end} of ${this.partnerTotalCount}`;
  }

  getWellName(record: PolicyRecordDto): string {
    return record.fields?.['well_name'] ?? 'Unknown Well';
  }

  getApi(record: PolicyRecordDto): string {
    return record.fields?.['api'] ?? '';
  }

  getFieldName(record: PolicyRecordDto): string {
    return record.fields?.['field_name'] ?? '';
  }

  getDate(value: any): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }

  get rangeLabel(): string {
    if (this.totalCount === 0) return '0 records';
    const start = (this.page - 1) * this.pageSize + 1;
    const end   = Math.min(this.page * this.pageSize, this.totalCount);
    return `${start}–${end} of ${this.totalCount}`;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) return;
    this.page = page;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.page = 1;
  }

  onSearchChange(value: string): void {
    this.searchTerm = value.trim();
    this.page = 1;

    if (this.searchTerm && this.filteredRecords.length === 0) {
      this.searchPolicyRecords(this.searchTerm);
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.page = 1;
  }

  // ── Partner CRUD ─────────────────────────────────────────────────────────

  @HostListener('document:click')
  onDocumentClick(): void {
    this.menuPartner = null;
  }

  openCreatePartner(): void {
    this.createPartnerMode = true;
    this.newPartnerCode = '';
    this.newPartnerName = '';
    this.newPartnerRefClientId = null;
    if (this.availableClients.length === 0) {
      this.partnerAccess.getAvailableClients(res => { this.availableClients = res; });
    }
  }

  cancelCreatePartner(): void {
    this.createPartnerMode = false;
  }

  submitCreatePartner(): void {
    if (!this.newPartnerCode.trim() || !this.newPartnerName.trim()) return;
    this.partnerAccess.createPartner(
      this.clientId,
      { entityCode: this.newPartnerCode.trim(), entityName: this.newPartnerName.trim(), refClientId: this.newPartnerRefClientId || null },
      () => {
        this.createPartnerMode = false;
        this.partnerPage = 1;
        this.loadPartners();
      }
    );
  }

  openPartnerMenu(partner: PartnerDto, event: MouseEvent): void {
    event.stopPropagation();
    this.menuPartner = this.menuPartner?.id === partner.id ? null : partner;
    this.menuX = event.clientX;
    this.menuY = event.clientY;
  }

  deletePartner(partner: PartnerDto): void {
    this.menuPartner = null;
    if (!confirm(`Delete partner "${partner.name}"?`)) return;
    this.partnerAccess.deletePartner(partner.id, () => {
      if (this.activePartnerId === partner.id) {
        this.activePartnerId = null;
        this.selectedPartnerIds.clear();
      }
      if (this.managingUsersPartner?.id === partner.id) this.managingUsersPartner = null;
      this.loadPartners();
    });
  }

  // ── User management ───────────────────────────────────────────────────────

  openManageUsers(partner: PartnerDto): void {
    this.menuPartner = null;
    this.managingUsersPartner = partner;
    this.userToAdd = '';
    this.partnerUsers = [];
    this.availableUsers = [];
    this.loadPartnerUsers(partner);
    this.loadAvailableUsers(partner);
    setTimeout(() => this.usersPanelRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }

  closeManageUsers(): void {
    this.managingUsersPartner = null;
  }

  openAssignClient(partner: PartnerDto): void {
    this.menuPartner = null;
    this.assigningClientPartner = partner;
    this.assignClientRefId = partner.refClientId ?? null;
    if (this.availableClients.length === 0) {
      this.partnerAccess.getAvailableClients(res => { this.availableClients = res; });
    }
    setTimeout(() => this.assignPanelRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }

  closeAssignClient(): void {
    this.assigningClientPartner = null;
    this.assignClientRefId = null;
  }

  submitAssignClient(): void {
    if (!this.assigningClientPartner) return;
    const partner = this.assigningClientPartner;
    this.partnerAccess.assignClient(partner.id, this.assignClientRefId, () => {
      const p = this.allPartners.find(x => x.id === partner.id);
      if (p) {
        p.refClientId = this.assignClientRefId ?? undefined;
        p.refClientName = this.availableClients.find(c => c.id === this.assignClientRefId)?.displayName;
      }
      this.closeAssignClient();
    });
  } 

  private loadPartnerUsers(partner: PartnerDto): void {
    this.partnerAccess.getPartnerUsers(partner.id, users => {
      this.partnerUsers = users;
    });
  }

  private loadAvailableUsers(partner: PartnerDto): void {
    this.partnerAccess.getAssignableUsersForPartner(partner.id, this.clientId, users => {
      this.availableUsers = users;
    });
  }

  get addableUsers(): PartnerUserDto[] {
    return this.availableUsers;
  }

  addUser(): void {
    if (!this.userToAdd || !this.managingUsersPartner) return;
    this.partnerAccess.addPartnerUser(this.managingUsersPartner.id, this.userToAdd, () => {
      this.userToAdd = '';
      this.loadPartnerUsers(this.managingUsersPartner!);
    });
  }

  removeUser(userId: string): void {
    if (!this.managingUsersPartner) return;
    this.partnerAccess.removePartnerUser(this.managingUsersPartner.id, userId, () => {
      this.loadPartnerUsers(this.managingUsersPartner!);
    });
  }

  onPartnerSearchChange(value: string): void {
    this.partnerSearchTerm = value.trim();
    this.partnerPage = 1;
  }

  clearPartnerSearch(): void {
    this.partnerSearchTerm = '';
    this.partnerPage = 1;
  }
  selectRecordForAttachment(recordId: string): void {
    this.persistCurrentRecordSelections();

    const key = recordId.toLowerCase();
    this.selectedRecordId = key;

    const partnerId = this.activePartnerId;
    if (!partnerId) return;

    const hMap = this.recordHierarchySelections.get(partnerId);
    const aMap = this.recordAttachmentSelections.get(partnerId);

    if (hMap?.has(key)) {
      this.selectedHierarchyIds = new Set(hMap.get(key));
      this.selectedAttachmentIds = new Set(aMap?.get(key) ?? []);
      this.loadFolderTree(key);
      return;
    }

    this.partnerAccess.getAccessNodesByRecord(
      partnerId,
      key,
      res => {
        this.selectedHierarchyIds = new Set(
          (res.hierarchies ?? []).filter(x => !x.negate).map(x => x.id)
        );
        this.selectedAttachmentIds = new Set(
          (res.attachments ?? []).filter(x => !x.negate).map(x => x.id)
        );

        let hMap = this.recordHierarchySelections.get(partnerId);
        if (!hMap) { hMap = new Map(); this.recordHierarchySelections.set(partnerId, hMap); }
        hMap.set(key, new Set(this.selectedHierarchyIds));

        let aMap = this.recordAttachmentSelections.get(partnerId);
        if (!aMap) { aMap = new Map(); this.recordAttachmentSelections.set(partnerId, aMap); }
        aMap.set(key, new Set(this.selectedAttachmentIds));

        this.loadFolderTree(key);
      }
    );
  }

  private refreshRecordAccessNodes(partnerId: string, recordId: string): void {
    const key = recordId.toLowerCase();

    this.partnerAccess.getAccessNodesByRecord(
      partnerId,
      key,
      res => {
        this.selectedHierarchyIds = new Set(
          (res.hierarchies ?? []).filter(x => !x.negate).map(x => x.id)
        );

        this.selectedAttachmentIds = new Set(
          (res.attachments ?? []).filter(x => !x.negate).map(x => x.id)
        );

        let hMap = this.recordHierarchySelections.get(partnerId);
        if (!hMap) { hMap = new Map(); this.recordHierarchySelections.set(partnerId, hMap); }
        hMap.set(key, new Set(this.selectedHierarchyIds));

        let aMap = this.recordAttachmentSelections.get(partnerId);
        if (!aMap) { aMap = new Map(); this.recordAttachmentSelections.set(partnerId, aMap); }
        aMap.set(key, new Set(this.selectedAttachmentIds));
      }
    );
  }
}
